import AxiosStatic, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { API_BASE_URL } from '../../constants/endpoints';
import { GetOptions, PostOptions } from './types';
import { buildFormData, buildUrl } from './utils';
import { getEncryptedItem, removeEncryptedItem, setEncryptedItem } from '@/utils/LocalStorageHelper';

const axios = AxiosStatic.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'      
  },
  responseType: 'json',
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

export class API {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expires: Date | null = null;
  private companyId: number | null = null;
  constructor() {
    if (typeof window !== 'undefined') {
      const accessToken = getEncryptedItem<string>('accessToken');
      if (accessToken) {
        this.accessToken = accessToken;
      }
      const refreshToken = getEncryptedItem<string>('refreshToken');
      if (refreshToken) {
        this.refreshToken = refreshToken;
      }

      const expires = getEncryptedItem<string>('expires');
      if (expires) {
        this.expires = new Date(expires);
      }
      const companyId = getEncryptedItem<string>('companyId');
      if (companyId) {
        this.companyId = parseInt(companyId);        
      }
    }

    axios.interceptors.request.use(async (config) => {
      if (this.accessToken) {
        config.headers.Authorization = `Bearer ${this.accessToken}`;
      }
      if (this.companyId) {
        config.headers['X-Company-Id'] = this.companyId.toString();
      }

      return config;
    });

    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401&&originalRequest?.url?.includes('/Auth/refresh-token')) { 
          this.clearToken();
          window.location.href = '/login';
        }
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          this.refreshToken
        ) {
          if (isRefreshing) {
            return new Promise((resolve) => {
              refreshSubscribers.push((token: string) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                resolve(axios(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const { data } = await this.refreshAccessToken();           
            this.setToken(data.data.token, data.data.refreshToken, new Date(data.data.expires));
            originalRequest.headers['Authorization'] = `Bearer ${data.data.token}`;
            onRefreshed(data.data.token);
            isRefreshing = false;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error('Refresh token error:', refreshError);
            isRefreshing = false;
            this.clearToken();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken() {
    try {
      const response = await axios.post('/Auth/refresh-token', this.refreshToken);
      return response;
    } catch (error) {
      console.error('Refresh token failed', error);
      throw error;
    }
  }

  setToken(accessToken: string,refreshToken: string, expires: Date) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.expires = expires;

    setEncryptedItem('accessToken', accessToken);
    setEncryptedItem('expires', expires.toString());
    setEncryptedItem('refreshToken', refreshToken);

  }

  clearToken() {
    this.accessToken = null;
    this.refreshToken = null;
    this.expires = null;

    removeEncryptedItem('accessToken');
    removeEncryptedItem('expires');
    removeEncryptedItem('refreshToken');
    removeEncryptedItem('companies');
  }
  

  get hasToken() {  
    return this.accessToken !== null;
  }

  request<T = any, R = AxiosResponse<T>, D = any>(config: AxiosRequestConfig<D>) {
    if (!config.url) {
      throw new Error('`url` is required');
    }

    return axios.request<T, R, D>(config);
  }
}

(['delete', 'get', 'head', 'options'] as const).forEach((method) => {
  API.prototype[method] = function <T = any, R = AxiosResponse<T>, D = any>({
    urlName,
    ids,
    params,
    config,
  }: GetOptions<D>) {
    return this.request<T, R, D>({
      ...config,
      ...(params ? { params } : {}),
      url: buildUrl(urlName, ids),
      method,
    });
  };
});

(['post', 'put', 'patch'] as const).forEach((method) => {
  API.prototype[method] = function <T = any, R = AxiosResponse<T>, D = any>({
    urlName,
    ids,
    data,
    params,
    config,
  }: PostOptions<D>) {
    return this.request<T, R, D>({
      ...config,
      ...(params ? { params } : {}),
      url: buildUrl(urlName, ids),
      method,
      data,
    });
  };

  const postMethod = `${method}Form` as 'postForm' | 'putForm' | 'patchForm';
  API.prototype[postMethod] = function <
    T = any,
    R = AxiosResponse<T>,
    D extends Record<string, any> = Record<string, any>,
  >({ urlName, ids, data, params, config }: PostOptions<D>) {
    return this.request<T, R, D>({
      ...config,
      ...(params ? { params } : {}),
      url: buildUrl(urlName, ids),
      method,
      data: buildFormData(data) as unknown as D,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };
});

export interface API {
  /**
   * Makes a DELETE request to the API.
   *
   * @memberof API
   * @method delete
   */
  delete: <T = any>(options: GetOptions) => Promise<AxiosResponse<T>>;

  /**
   * Makes a GET request to the API.
   *
   * @memberof API
   * @method get
   */
  get: <T = any>(options: GetOptions) => Promise<AxiosResponse<T>>;

  /**
   * Makes a HEAD request to the API.
   *
   * @memberof API
   * @method head
   */
  head: <T = any>(options: GetOptions) => Promise<AxiosResponse<T>>;

  /**
   * Makes a OPTIONS request to the API.
   *
   * @memberof API
   * @method options
   */
  options: <T = any>(options: GetOptions) => Promise<AxiosResponse<T>>;

  /**
   * Makes a POST request to the API.
   *
   * @memberof API
   * @method post
   */
  post: <T = any, D = any>(options: PostOptions<D>) => Promise<AxiosResponse<T>>;

  /**
   * Makes a PUT request to the API.
   *
   * @memberof API
   * @method put
   */
  put: <T = any, D = any>(options: PostOptions<D>) => Promise<AxiosResponse<T>>;

  /**
   * Makes a PATCH request to the API.
   *
   * @memberof API
   * @method patch
   */
  patch: <T = any, D = any>(options: PostOptions<D>) => Promise<AxiosResponse<T>>;

  /**
   * Makes a POST request to the API with form data. Usefull for file uploads.
   *
   * @memberof API
   * @method postForm
   * @see https://developer.mozilla.org/en-US/docs/Web/API/FormData
   */
  postForm: <T = any, D extends Record<string, any> = Record<string, any>>(
    options: PostOptions<D>
  ) => Promise<AxiosResponse<T>>;

  /**
   * Makes a PUT request to the API with form data. Usefull for file uploads.
   *
   * @memberof API
   * @method putForm
   * @see https://developer.mozilla.org/en-US/docs/Web/API/FormData
   */
  putForm: <T = any, D extends Record<string, any> = Record<string, any>>(
    options: PostOptions<D>
  ) => Promise<AxiosResponse<T>>;

  /**
   * Makes a PATCH request to the API with form data. Usefull for file uploads.
   *
   * @memberof API
   * @method patchForm
   * @see https://developer.mozilla.org/en-US/docs/Web/API/FormData
   */
  patchForm: <T = any, D extends Record<string, any> = Record<string, any>>(
    options: PostOptions<D>
  ) => Promise<AxiosResponse<T>>;
}
